import { UploadData } from '../../../../store/uploadStore';
import { removePathPrefix } from '../../../../libs/removePathPrefix';
import { ValidationCheck } from '../../../../consts/validationCheck';
import { PilotApiValidationResult } from '../../../../types';

// For finicky checks (BlurScore, PanoToScan, ScanToPano) we require at least 5 errors
// in addition to the config requirements, otherwise we will see random blinking.
const MINIMAL_GROUP_VALIDATION_COUNT_THRESHOLD = 5;

export interface FilteredValidations {
  photo: string;
  validation: Partial<Record<ValidationCheck, PilotApiValidationResult>>;
}

/**
 * Computes the number of failed validations per check.
 */
const computeFailCounts = (validations: FilteredValidations[]): Partial<Record<ValidationCheck, number>> => {
  const counts: Partial<Record<ValidationCheck, number>> = {};
  for (const { validation } of validations) {
    const v = validation || {};
    for (const [checkKey, result] of Object.entries(v)) {
      if (!result[0]) {
        const check = checkKey as ValidationCheck;
        counts[check] = (counts[check] || 0) + 1;
      }
    }
  }
  return counts;
};

/**
 * Determines if each validation check fails beyond its threshold based on group error counts.
 * The configuration (Validation.config) is assumed to exist in scope.
 */
const haveChecksBrokenThresholds = (validations: FilteredValidations[]): Record<ValidationCheck, boolean> => {
  const failCounts = computeFailCounts(validations);
  const totalValidations = validations.length;
  const thresholds: Record<ValidationCheck, boolean> = {} as Record<ValidationCheck, boolean>;

  for (const check of Object.values(ValidationCheck)) {
    const failedCount = failCounts[check] || 0;
    let isBroken = false;
    switch (check) {
      case ValidationCheck.PanoToScanMissupload:
        isBroken =
          failedCount >= MINIMAL_GROUP_VALIDATION_COUNT_THRESHOLD &&
          failedCount / totalValidations > Validation.config.panoErrorThreshold;
        break;
      case ValidationCheck.ScanToPanoMissupload:
        isBroken =
          failedCount >= MINIMAL_GROUP_VALIDATION_COUNT_THRESHOLD &&
          failedCount / totalValidations > Validation.config.scanErrorThreshold;
        break;
      case ValidationCheck.BlurScore:
        isBroken =
          failedCount >= MINIMAL_GROUP_VALIDATION_COUNT_THRESHOLD &&
          (failedCount / totalValidations) * 100 > Validation.config.blurShowAlertPercentage;
        break;
      default:
        isBroken = true;
        break;
    }
    thresholds[check] = isBroken;
  }
  return thresholds;
};

/**
 * Combines validations from photos and queue, then filters out individual validation checks
 * for which the group-level threshold is not broken. This version avoids using Immer.
 */
export const getFilteredValidations = (upload: UploadData): FilteredValidations[] => {
  // Combine validations from both upload photos and queue.
  const combinedValidations: FilteredValidations[] = [
    ...((upload.upload?.photos || []).map(({ name, validation }) => ({
      photo: removePathPrefix(name),
      validation: validation || {},
    })) as FilteredValidations[]),
    ...upload.queue
      .filter(({ validation }) => !!validation)
      .map(({ name, validation }) => ({
        photo: removePathPrefix(name),
        validation: validation!,
      })),
  ];

  const thresholds = haveChecksBrokenThresholds(combinedValidations);

  return combinedValidations.map((item) => {
    const originalValidation = item.validation || {};
    const filteredValidation: Partial<Record<ValidationCheck, PilotApiValidationResult>> = {};
    for (const key in originalValidation) {
      const check = key as ValidationCheck;
      if (thresholds[check]) {
        filteredValidation[check] = originalValidation[check];
      }
    }
    return {
      photo: item.photo,
      validation: filteredValidation,
    };
  });
};
